import { Controller } from "@hotwired/stimulus";

// The home page's search box shows the three trip types and "Från" /
// "Vart vill du åka?", and unfolds into the full form --
// class, dates, travellers, direct-flights, the search button -- when the
// customer has said something the rest of the form depends on. Two things
// do that, and nothing else:
//
//   - picking a destination from the autocomplete list
//     (autocomplete:selected, not a click or a focus: the field is the
//     first thing anyone touches, and opening the form while they are
//     mid-word moved everything under their hands)
//   - clicking a trip type, since Enkel and Multi-City change which fields
//     the form should even have
//
// Attached only here (ApplicationHelper#collapsible_searchfield?); step1's
// "Ändra sökning" panel renders the same partial whole.
//
// Phones collapse too, with the same two triggers. Only the column count
// is desktop-only (a phone stacks the fields in one column at every stage,
// so there is nothing to widen); what changes there is purely the height.
//
// One-way: once the form is open it stays open. Re-collapsing on blur would
// mean a field disappearing out from under someone who clicked away to think
// about a date, and nothing here is expensive enough to be worth hiding
// again.
export default class extends Controller {
  static targets = ["collapsible", "grid"];

  // Roughly a quarter of a second: enough to read as the form unfolding
  // rather than the page jumping, short enough that nobody waits for it.
  static DURATION = 250;

  expand() {
    if (this.expanded) return;
    this.expanded = true;

    // Measured before and after revealing, because there is no CSS way to
    // transition to height:auto and the form grows by an amount only the
    // browser knows -- two extra rows at whatever height their contents
    // need, in whatever locale.
    const startHeight = this.element.getBoundingClientRect().height;

    this.collapsibleTargets.forEach((el) => el.classList.remove("hidden"));

    // The third column comes back in one frame, before the height is
    // measured below, so what is measured is the layout the form actually
    // ends in. Swapped explicitly rather than by letting one class win over
    // the other: `important: true` makes both utilities !important, so
    // which of them applied would come down to their order in the compiled
    // stylesheet, not to anything stated here.
    this.gridTargets.forEach((el) => {
      el.classList.remove("lg:grid-cols-2");
      el.classList.add("lg:grid-cols-3");
    });

    this.animateHeight(startHeight);
  }

  // Grows the form from the height it had to the height it now wants, with
  // the overflow clipped so the new rows are revealed by the edge moving
  // rather than appearing on top of the photo behind the box.
  //
  // Web Animations rather than an inline style plus a transitionend
  // listener: nothing is left on the element to clean up afterwards, so a
  // dropped event cannot strand the form at a fixed pixel height.
  animateHeight(startHeight) {
    const endHeight = this.element.getBoundingClientRect().height;
    if (endHeight === startHeight) return;
    if (this.reducedMotion || !this.element.animate) return;

    const previousOverflow = this.element.style.overflow;
    this.element.style.overflow = "hidden";

    const animation = this.element.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      { duration: this.constructor.DURATION, easing: "ease-out" }
    );
    // onfinish/oncancel rather than the `finished` promise: the promise is
    // the newer half of the API, and this has to put the overflow back even
    // on the browsers that only have the older half.
    const restore = () => {
      this.element.style.overflow = previousOverflow;
    };
    animation.onfinish = restore;
    animation.oncancel = restore;
  }

  // Someone who has asked their system to stop animating things gets the
  // form open immediately, which is the same end state.
  get reducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
}
